import { ref, onMounted, onBeforeUnmount,toValue, watch  } from '#imports';
import type {Ref} from '#imports';


/**
 * 文件上传处理函数
 * @param files 文件列表
 */
export type FileUploadHandler = (files: File[]) => void;

/**
 * 粘贴上传配置选项
 */
export interface UsePasteUploadOptions {
  /**
   * 文件上传处理函数
   */
  onFiles: FileUploadHandler;

  /**
   * 允许的文件类型（MIME类型）
   * @default ['image/*'] 默认允许所有图片类型
   */
  allowedTypes?: string[];

  /**
   * 最大文件大小（字节）
   * @default 10 * 1024 * 1024 (10MB)
   */
  maxFileSize?: number;

  /**
   * 粘贴目标元素引用
   */
  pasteTargetRef?: () => HTMLElement|undefined;

  /**
   * 是否在挂载时自动启用
   * @default true
   */
  enable?: boolean;
  useCapture?: boolean;
}

/**
 * 粘贴上传 Hook
 * @param options 配置选项
 * @returns 启用和禁用粘贴上传的方法
 */
export function usePasteUpload(options: UsePasteUploadOptions) {
  const {
    onFiles,
    allowedTypes = ['image/*'],
    maxFileSize = 10 * 1024 * 1024, // 10MB
    pasteTargetRef,
    enable = true,
    useCapture = false
  } = options;

  // 处理粘贴事件
  const handlePaste = (e: any) => {
    const event = e as ClipboardEvent;
    if (!event.clipboardData || !event.clipboardData.items) return;

    const items = Array.from(event.clipboardData.items);
    const fileItems = items.filter(item => item.kind === 'file');

    if (fileItems.length > 0) {
      const files: File[] = [];

      fileItems.forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      });

      if (files.length > 0) {
        const validFiles = filterFiles(files);
        if (validFiles.length > 0) {
          e.stopPropagation();
          e.preventDefault();
          onFiles(validFiles);
        }
      }
    }
  };

  // 过滤文件
  const filterFiles = (files: File[]): File[] => {
    return files.filter(file => {
      // 检查文件类型
      const isTypeAllowed = allowedTypes.some(type => {
        if (type.endsWith('/*')) {
          const category = type.split('/')[0];
          return file.type.startsWith(`${category}/`);
        }
        return file.type === type;
      });

      // 检查文件大小
      const isSizeValid = file.size <= maxFileSize;

      return isTypeAllowed && isSizeValid;
    });
  };

  // 启用粘贴上传
  const bindEvent = (target?: HTMLElement|undefined) => {
    target = target || toValue(pasteTargetRef);
    if (!target) return;
    target.addEventListener('paste', handlePaste, useCapture);
  };

  // 禁用粘贴上传
  const unbindEvent = (target?: HTMLElement|undefined) => {
    target = target || toValue(pasteTargetRef);
    if (!target) return;
    target.removeEventListener('paste', handlePaste, useCapture);
  };

  watch(() => toValue(pasteTargetRef), (newTarget,oldTarget) => {
    if (oldTarget) {
      unbindEvent(oldTarget);
    }
    if (newTarget && enable) {
      bindEvent(newTarget);
    }

  },{
    immediate: true
  });

  // 自动清理
  onBeforeUnmount(() => {
    unbindEvent();
  });

  return {
    enable,
    unbindEvent
  };
}
